/**
 * Aircraft performance. ASSUMPTIONS, not certified data: round numbers chosen to look right
 * on a radar scope and in the tower view. Every number the flight model uses is here.
 *
 * Category tables are used at low realism; the per-type table (falling back to the
 * category) at mid/high. Speeds are indicated airspeed in knots; rates in ft/min.
 */
import { categoryForType, type AircraftCategory } from '@atc/core';

export interface Performance {
  /** Rotation speed on take-off. */
  vrKt: number;
  /** Final approach speed (flown from ~8 nm to touchdown). */
  approachKt: number;
  /** Slowest speed the pilot accepts airborne (clean-ish configuration). */
  minKt: number;
  /** Fastest speed the pilot accepts below 10 000 ft (regulatory 250) and above. */
  maxLowKt: number;
  maxKt: number;
  /** Default speed flown when the controller has not assigned one, below / above 10 000 ft. */
  cruiseLowKt: number;
  cruiseKt: number;
  /** Climb rate at sea level and at 20 000 ft (linear in between, floor at 500). */
  climbFpmSeaLevel: number;
  climbFpm20k: number;
  descentFpm: number;
  /** Speed change rates, kt per second, airborne. */
  accelKtS: number;
  decelKtS: number;
  /** Take-off roll acceleration and landing deceleration, kt per second. */
  rollAccelKtS: number;
  rolloutDecelKtS: number;
  /** Bank angle used for turns at mid/high realism. */
  bankDeg: number;
  /** Speed at which the aircraft exits the runway after landing. */
  exitKt: number;
}

export const CATEGORY_PERFORMANCE: Record<AircraftCategory, Performance> = {
  heavy: { vrKt: 165, approachKt: 145, minKt: 180, maxLowKt: 250, maxKt: 320, cruiseLowKt: 250, cruiseKt: 300, climbFpmSeaLevel: 2200, climbFpm20k: 1300, descentFpm: 2000, accelKtS: 1.5, decelKtS: 1.2, rollAccelKtS: 3.2, rolloutDecelKtS: 4.0, bankDeg: 25, exitKt: 30 },
  medium: { vrKt: 145, approachKt: 138, minKt: 170, maxLowKt: 250, maxKt: 310, cruiseLowKt: 250, cruiseKt: 290, climbFpmSeaLevel: 2800, climbFpm20k: 1600, descentFpm: 2200, accelKtS: 2.0, decelKtS: 1.5, rollAccelKtS: 4.0, rolloutDecelKtS: 4.5, bankDeg: 25, exitKt: 30 },
  light: { vrKt: 60, approachKt: 75, minKt: 70, maxLowKt: 160, maxKt: 180, cruiseLowKt: 110, cruiseKt: 130, climbFpmSeaLevel: 800, climbFpm20k: 300, descentFpm: 800, accelKtS: 1.5, decelKtS: 1.5, rollAccelKtS: 3.0, rolloutDecelKtS: 4.0, bankDeg: 20, exitKt: 20 },
};

/** Per-type overrides (partial); the rest comes from the category. */
const TYPE_PERFORMANCE: Record<string, Partial<Performance>> = {
  B738: { vrKt: 150, approachKt: 142, climbFpmSeaLevel: 3000 },
  B739: { vrKt: 155, approachKt: 145 },
  B737: { vrKt: 145, approachKt: 138 },
  A320: { vrKt: 145, approachKt: 137, climbFpmSeaLevel: 2900 },
  A319: { vrKt: 140, approachKt: 132 },
  A321: { vrKt: 155, approachKt: 142, climbFpmSeaLevel: 2600 },
  A20N: { vrKt: 145, approachKt: 137, climbFpmSeaLevel: 3000 },
  A21N: { vrKt: 155, approachKt: 142 },
  E175: { vrKt: 135, approachKt: 128, climbFpmSeaLevel: 3200 },
  E190: { vrKt: 140, approachKt: 132 },
  CRJ9: { vrKt: 140, approachKt: 135, climbFpmSeaLevel: 3200 },
  CRJ7: { vrKt: 138, approachKt: 133 },
  DH8D: { vrKt: 115, approachKt: 120, minKt: 140, maxKt: 280, cruiseKt: 270, climbFpmSeaLevel: 2000 },
  B77W: { vrKt: 170, approachKt: 150, climbFpmSeaLevel: 2400 },
  B772: { vrKt: 165, approachKt: 145 },
  B788: { vrKt: 160, approachKt: 142, climbFpmSeaLevel: 2600 },
  B789: { vrKt: 165, approachKt: 145, climbFpmSeaLevel: 2500 },
  B744: { vrKt: 175, approachKt: 155, climbFpmSeaLevel: 2000 },
  B748: { vrKt: 175, approachKt: 155 },
  B763: { vrKt: 160, approachKt: 140 },
  A332: { vrKt: 160, approachKt: 140 },
  A333: { vrKt: 165, approachKt: 142 },
  A359: { vrKt: 165, approachKt: 142, climbFpmSeaLevel: 2600 },
  A388: { vrKt: 165, approachKt: 140, climbFpmSeaLevel: 1800, rolloutDecelKtS: 3.5 },
  MD11: { vrKt: 170, approachKt: 155 },
  C172: { vrKt: 55, approachKt: 65, minKt: 60, maxLowKt: 120, maxKt: 125, cruiseLowKt: 100, cruiseKt: 110, climbFpmSeaLevel: 700, climbFpm20k: 200 },
  C182: { vrKt: 60, approachKt: 70, cruiseLowKt: 120, cruiseKt: 135 },
  SR22: { vrKt: 70, approachKt: 80, minKt: 75, maxLowKt: 180, maxKt: 185, cruiseLowKt: 150, cruiseKt: 170, climbFpmSeaLevel: 1200, climbFpm20k: 500 },
  PA28: { vrKt: 60, approachKt: 65, minKt: 60, maxLowKt: 130, maxKt: 140, cruiseLowKt: 105, cruiseKt: 115, climbFpmSeaLevel: 650, climbFpm20k: 150 },
  BE58: { vrKt: 85, approachKt: 95, minKt: 90, maxLowKt: 200, maxKt: 200, cruiseLowKt: 170, cruiseKt: 180, climbFpmSeaLevel: 1600, climbFpm20k: 600 },
  C56X: { vrKt: 110, approachKt: 110, minKt: 120, cruiseKt: 300, climbFpmSeaLevel: 3500, climbFpm20k: 2000 },
  C750: { vrKt: 120, approachKt: 115, minKt: 130, cruiseKt: 320, climbFpmSeaLevel: 4000, climbFpm20k: 2500 },
  LJ35: { vrKt: 120, approachKt: 125, minKt: 130, cruiseKt: 320, climbFpmSeaLevel: 4000, climbFpm20k: 2200 },
};

export function performanceFor(type: string, typeSpecific: boolean): Performance {
  const cat = CATEGORY_PERFORMANCE[categoryForType(type)];
  if (!typeSpecific) return cat;
  const o = TYPE_PERFORMANCE[type.toUpperCase()];
  return o ? { ...cat, ...o } : cat;
}

export const EXPEDITE_FACTOR = 2;
/** Standard-rate turn, degrees per second. */
export const STANDARD_TURN_DEG_S = 3;
export const G_MPS2 = 9.80665;

/** Turn rate in deg/s at true airspeed `tasKt`: min(standard rate, bank-limited). */
export function turnRateDegS(tasKt: number, bankDeg: number, bankLimited: boolean): number {
  if (!bankLimited) return STANDARD_TURN_DEG_S;
  const v = Math.max(30, tasKt) * 0.514444;
  const omega = (G_MPS2 * Math.tan((bankDeg * Math.PI) / 180)) / v; // rad/s
  return Math.min(STANDARD_TURN_DEG_S, (omega * 180) / Math.PI);
}

/** Climb rate available at altitude. */
export function climbRateFpm(p: Performance, altFt: number, altitudeDependent: boolean): number {
  if (!altitudeDependent) return p.climbFpmSeaLevel;
  const t = Math.min(1, Math.max(0, altFt / 20000));
  return Math.max(500, p.climbFpmSeaLevel + (p.climbFpm20k - p.climbFpmSeaLevel) * t);
}

/** True airspeed from indicated: ~2 % per 1 000 ft (standard atmosphere approximation). */
export function tasFromIas(iasKt: number, altFt: number): number {
  return iasKt * (1 + 0.02 * Math.max(0, altFt) / 1000);
}
